import React, { useState, useEffect } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';
export default function ProjectTravel({ project, updateProject }) {
    const [newBlock, setNewBlock] = useState({ hotelName: '', cutoffDate: '', roomsBlocked: '', rate: '', link: '', notes: '' });
    const [newTransport, setNewTransport] = useState({ company: '', vehicle: '', capacity: '', time: '', route: '' });

    // State for hotel suggestions from Google Maps
    const [hotelSuggestions, setHotelSuggestions] = useState([]);
    const [isSearchingHotels, setIsSearchingHotels] = useState(false);

    const roomBlocks = project.roomBlocks || [];
    const transportation = project.transportation || [];

    const addRoomBlock = (e) => {
        e.preventDefault();
        if (!newBlock.hotelName) return;
        updateProject(project.id, { 
            roomBlocks: [...roomBlocks, { id: Date.now().toString(), ...newBlock }] 
        });
        setNewBlock({ hotelName: '', cutoffDate: '', roomsBlocked: '', rate: '', link: '', notes: '' });
    };

    const removeRoomBlock = (id) => {
        updateProject(project.id, { roomBlocks: roomBlocks.filter(b => b.id !== id) });
    };

    const addTransport = (e) => {
        e.preventDefault();
        if (!newTransport.company || !newTransport.time) return;
        updateProject(project.id, { 
            transportation: [...transportation, { id: Date.now().toString(), ...newTransport }] 
        });
        setNewTransport({ company: '', vehicle: '', capacity: '', time: '', route: '' });
    };

    const removeTransport = (id) => {
        updateProject(project.id, { transportation: transportation.filter(t => t.id !== id) });
    };

    // Stays Integration using Google Places API
    const handleFindHotels = () => {
        if (!window.google || !window.google.maps) {
            alert("Google Maps is not loaded yet. Please try again in a moment.");
            return;
        }
        setIsSearchingHotels(true);
        setHotelSuggestions([]);
        
        const geocoder = new window.google.maps.Geocoder();
        const address = project.venueAddress || project.location;
        
        if (!address || address === 'TBD') {
            alert("Please set an event location or venue address in the Overview tab first.");
            setIsSearchingHotels(false);
            return;
        }

        geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const loc = results[0].geometry.location;
                // Create a headless div for PlacesService
                const mapDiv = document.createElement('div');
                const service = new window.google.maps.places.PlacesService(mapDiv);
                
                const request = {
                    location: loc,
                    radius: '15000', // ~10 miles
                    type: ['lodging']
                };

                service.nearbySearch(request, (places, searchStatus) => {
                    if (searchStatus === window.google.maps.places.PlacesServiceStatus.OK && places) {
                        const suggestions = places
                            .sort((a, b) => (b.rating || 0) - (a.rating || 0))
                            .slice(0, 8)
                            .map(p => ({
                                name: p.name,
                                address: p.vicinity,
                                rating: p.rating,
                                reviews: p.user_ratings_total,
                                priceLevel: p.price_level,
                                photoUrl: p.photos && p.photos.length > 0 ? p.photos[0].getUrl({ maxWidth: 600 }) : 'https://placehold.co/600x400/e2e8f0/64748b?text=No+Image',
                                url: `https://www.google.com/travel/hotels/search?q=${encodeURIComponent(p.name + ' ' + p.vicinity)}`
                            }));
                        setHotelSuggestions(suggestions);
                    } else {
                        alert("No hotels found nearby.");
                    }
                    setIsSearchingHotels(false);
                });
            } else {
                alert("Could not locate the event address on the map.");
                setIsSearchingHotels(false);
            }
        });
    };

    return (
        <div className="space-y-10 animate-fade-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4 md:p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h3 className="text-blue-900 font-bold text-lg flex items-center gap-2"><span>🗺️</span> Accommodations & Travel</h3>
                    <p className="text-blue-700 text-sm mt-0.5">Manage hotel room blocks, cutoff dates, and guest shuttle transportation.</p>
                </div>
            </div>

            {/* Room Blocks Section */}
            <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <h3 className="font-bold text-lg text-gray-900">Hotel Room Blocks</h3>
                    <button 
                        onClick={handleFindHotels} 
                        disabled={isSearchingHotels}
                        className="text-xs bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg transition flex items-center gap-2 shadow-md disabled:opacity-50"
                    >
                        {isSearchingHotels ? <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span> : '🏨'} 
                        {isSearchingHotels ? 'Searching Local Stays...' : 'Find Nearby Stays'}
                    </button>
                </div>

                {/* Hotel Stays Integration UI */}
                {hotelSuggestions.length > 0 && (
                    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 animate-fade-in">
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2"><span>✨</span> Top Rated Local Stays</h4>
                            <button onClick={() => setHotelSuggestions([])} className="text-xs font-bold text-gray-400 hover:text-red-500 uppercase tracking-widest bg-white px-3 py-1 rounded-md border border-gray-200 shadow-sm transition">Close</button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                            {hotelSuggestions.map((hotel, idx) => (
                                <div key={idx} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-xl hover:border-blue-300 transition-all duration-300 group flex flex-col">
                                    <div className="h-40 bg-gray-200 relative overflow-hidden">
                                        <img src={hotel.photoUrl} alt={hotel.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                        {hotel.priceLevel !== undefined && (
                                            <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded-md shadow-sm">
                                                {Array(hotel.priceLevel).fill('$').join('')}
                                            </div>
                                        )}
                                        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm">
                                            <span className="text-yellow-500 text-xs">★</span>
                                            <span className="text-xs font-bold text-gray-900">{hotel.rating || 'New'}</span>
                                            <span className="text-[10px] text-gray-600 font-medium">({hotel.reviews || 0})</span>
                                        </div>
                                    </div>
                                    <div className="p-4 flex flex-col flex-grow">
                                        <h5 className="font-bold text-gray-900 text-base mb-1 truncate" title={hotel.name}>{hotel.name}</h5>
                                        <p className="text-xs text-gray-500 truncate mb-4 flex items-center gap-1">
                                            <span className="text-blue-400">📍</span> {hotel.address}
                                        </p>
                                        <div className="mt-auto flex flex-col gap-2 pt-2 border-t border-gray-100">
                                            <a href={hotel.url} target="_blank" rel="noopener noreferrer" className="text-center text-xs font-bold text-blue-600 bg-blue-50 py-2.5 rounded-lg hover:bg-blue-100 transition shadow-sm flex justify-center items-center gap-1">
                                                Check Prices & Book <span>↗</span>
                                            </a>
                                            <button 
                                                onClick={() => {
                                                    setNewBlock(prev => ({ ...prev, hotelName: hotel.name, link: hotel.url }));
                                                    document.getElementById('room-block-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                }}
                                                className="text-center text-xs font-bold text-gray-700 bg-white border border-gray-200 py-2.5 rounded-lg hover:bg-gray-50 transition shadow-sm"
                                            >
                                                Add to Event Blocks
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                <form id="room-block-form" onSubmit={addRoomBlock} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Hotel Name</label>
                            <input type="text" value={newBlock.hotelName} onChange={e => setNewBlock({...newBlock, hotelName: e.target.value})} placeholder="e.g. The Marriott Downtown" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" required />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Cutoff Date</label>
                            <input type="date" value={newBlock.cutoffDate} onChange={e => setNewBlock({...newBlock, cutoffDate: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Nightly Rate ($)</label>
                            <input type="text" value={newBlock.rate} onChange={e => setNewBlock({...newBlock, rate: e.target.value})} placeholder="e.g. 199" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Rooms Blocked</label>
                            <input type="number" value={newBlock.roomsBlocked} onChange={e => setNewBlock({...newBlock, roomsBlocked: e.target.value})} placeholder="Total rooms" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Booking Link / Code</label>
                            <input type="text" value={newBlock.link} onChange={e => setNewBlock({...newBlock, link: e.target.value})} placeholder="URL or Group Code" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                        </div>
                        <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-bold shadow-sm h-[42px] transition">Add Room Block</button>
                    </div>
                </form>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {roomBlocks.map(block => (
                        <div key={block.id} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm relative group hover:border-blue-300 transition-colors">
                            <button onClick={() => removeRoomBlock(block.id)} className="absolute top-3 right-3 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition bg-gray-50 rounded-full p-1"><PlannerIcons.Trash /></button>
                            
                            <h4 className="font-bold text-lg text-gray-900 pr-6">{block.hotelName}</h4>
                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                <div className="bg-gray-50 p-2 rounded-lg border border-gray-100">
                                    <span className="text-[10px] text-gray-500 uppercase font-bold block">Rate</span>
                                    <span className="font-medium text-gray-800">${block.rate || 'N/A'}</span>
                                </div>
                                <div className={`p-2 rounded-lg border ${block.cutoffDate && new Date(block.cutoffDate) < new Date() ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}>
                                    <span className={`text-[10px] uppercase font-bold block ${block.cutoffDate && new Date(block.cutoffDate) < new Date() ? 'text-red-600' : 'text-gray-500'}`}>Cutoff Date</span>
                                    <span className={`font-medium ${block.cutoffDate && new Date(block.cutoffDate) < new Date() ? 'text-red-700' : 'text-gray-800'}`}>
                                        {block.cutoffDate ? new Date(block.cutoffDate).toLocaleDateString() : 'N/A'}
                                    </span>
                                </div>
                                <div className="bg-gray-50 p-2 rounded-lg border border-gray-100 col-span-2">
                                    <span className="text-[10px] text-gray-500 uppercase font-bold block">Rooms Held</span>
                                    <span className="font-medium text-gray-800">{block.roomsBlocked || 0} rooms</span>
                                </div>
                            </div>
                            {block.link && (
                                <div className="mt-3 text-sm bg-blue-50 text-blue-800 p-2 rounded-lg border border-blue-100 break-all">
                                    <strong>Link/Code:</strong> {block.link}
                                </div>
                            )}
                        </div>
                    ))}
                    {roomBlocks.length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-400 italic bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            No room blocks added.
                        </div>
                    )}
                </div>
            </div>

            {/* Transportation Section */}
            <div className="space-y-4">
                <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2">Shuttles & Transportation</h3>
                
                <form onSubmit={addTransport} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
                        <div className="md:col-span-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Company / Driver</label>
                            <input type="text" value={newTransport.company} onChange={e => setNewTransport({...newTransport, company: e.target.value})} placeholder="e.g. Elite Shuttles" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" required />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Vehicle Type</label>
                            <input type="text" value={newTransport.vehicle} onChange={e => setNewTransport({...newTransport, vehicle: e.target.value})} placeholder="e.g. 50-Pax Bus" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Pickup Time</label>
                            <input type="time" value={newTransport.time} onChange={e => setNewTransport({...newTransport, time: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" required />
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 items-end">
                        <div className="w-full flex-grow">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Route Details</label>
                            <input type="text" value={newTransport.route} onChange={e => setNewTransport({...newTransport, route: e.target.value})} placeholder="e.g. Hotel to Ceremony Venue" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <button type="submit" className="w-full md:w-auto bg-gray-900 hover:bg-black text-white px-8 py-2.5 rounded-lg text-sm font-bold shadow-sm h-[42px] transition whitespace-nowrap">Add Shuttle</button>
                    </div>
                </form>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-200">
                                <tr>
                                    <th className="p-4">Time</th>
                                    <th className="p-4">Company / Vehicle</th>
                                    <th className="p-4">Route Details</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {transportation.sort((a,b) => a.time.localeCompare(b.time)).map(t => (
                                    <tr key={t.id} className="hover:bg-gray-50 transition group">
                                        <td className="p-4">
                                            <span className="bg-blue-100 text-blue-800 font-bold px-2.5 py-1 rounded-md font-mono text-xs">
                                                {t.time}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <p className="font-bold text-gray-900">{t.company}</p>
                                            {t.vehicle && <p className="text-xs text-gray-500">{t.vehicle}</p>}
                                        </td>
                                        <td className="p-4 font-medium text-gray-700">{t.route || '--'}</td>
                                        <td className="p-4 text-right">
                                            <button onClick={() => removeTransport(t.id)} className="text-gray-400 hover:text-red-500 transition opacity-0 group-hover:opacity-100 p-1 bg-white rounded-full shadow-sm border border-gray-100">
                                                <PlannerIcons.Trash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {transportation.length === 0 && (
                                    <tr><td colSpan="4" className="p-8 text-center text-gray-400 italic">No transportation scheduled.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}